import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { CheckCircle2, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PaymentSuccess = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);

    // Track revenue with Owner's Hub analytics (dedupe via sessionStorage)
    try {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");
      const transactionId = sessionId || `txn_${Date.now()}`;
      const trackedKey = `_oh_revenue_tracked_${transactionId}`;

      if (!sessionStorage.getItem(trackedKey) && typeof (window as any).ohRevenue === "function") {
        (window as any).ohRevenue(
          5,
          transactionId,
          "Gamification Strategy",
          "USD",
          { source: "stripe_checkout", page: "payment_success" }
        );
        sessionStorage.setItem(trackedKey, "1");
      }
    } catch (e) {
      // Non-blocking — analytics failures should never break the page
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-primary/5 to-background">
      <Navigation />

      <section className="pt-32 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <Card className="p-12 shadow-2xl border-2 border-primary/20 text-center space-y-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 mx-auto">
              <CheckCircle2 className="w-12 h-12 text-primary" />
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl font-bold">Payment Successful!</h1>
              <p className="text-xl text-muted-foreground">
                Thank you for your purchase
              </p>
            </div>

            <div className="bg-primary/5 rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-center gap-3">
                <Mail className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-semibold">Check Your Email</h2>
              </div>
              <p className="text-muted-foreground">
                We've sent you an email with a unique link to access your assessment. 
                The email should arrive within the next 2 minutes.
              </p>
              <p className="text-sm text-muted-foreground">
                Can't find it? Check your spam folder or contact support.
              </p>
            </div>

            <div className="space-y-4 pt-4">
              <h3 className="text-lg font-semibold">What happens next?</h3>
              <ul className="text-left space-y-3 max-w-md mx-auto">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-semibold text-primary">1</span>
                  </div>
                  <span className="text-muted-foreground">Click the link in your email</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-semibold text-primary">2</span>
                  </div>
                  <span className="text-muted-foreground">Complete the 7-question assessment (10 min)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-semibold text-primary">3</span>
                  </div>
                  <span className="text-muted-foreground">Receive your strategy via email (2 min)</span>
                </li>
              </ul>
            </div>

            <Button 
              size="lg"
              onClick={() => navigate("/")}
              className="bg-gradient-to-r from-primary to-accent hover:opacity-90"
            >
              Return to Home
            </Button>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PaymentSuccess;
